import { useState, memo, useEffect } from "react";
import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderBlank, faFile, faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { ItemTypes } from "../../constants";

const TREE_PER_PAGE = 10;

const FolderTreeItem = memo(({
  item,
  getItemsByParentId,
  itemsCache,
  itemsTotals,
  onNavigate,
  onDrop,
  level = 0,
  openContextMenu,
  activeFolderId,
  activeFolderIds
}) => {
  const { t } = useTranslation();
  const isFolder = item?.type === 'folder';
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const isActive = item?.id === activeFolderId;
  const isInActivePath = activeFolderIds?.includes(item?.id);

  useEffect(() => {
    if (isFolder && isActive) {
      setIsExpanded(true);
    }
  }, [isFolder, isActive]);

  useEffect(() => {
    if (isFolder && isInActivePath) {
      setIsExpanded(true);
    }
  }, [isFolder, isInActivePath]);

  const [{ isOver }, drop] = useDrop({
    accept: [ItemTypes.REPORT, ItemTypes.FOLDER],
    canDrop: () => isFolder,
    drop: (draggedItem) => {
      if (draggedItem.id !== item.id && isFolder) {
        onDrop(draggedItem.id, item.id);
      }
    },
    collect: (monitor) => ({
      isOver: isFolder ? monitor.isOver() : false,
    }),
  });

  useEffect(() => {
    if (isFolder && isExpanded && !itemsCache[item.id]) {
      const controller = new AbortController();
      getItemsByParentId(item.id, controller, 1, TREE_PER_PAGE);
      return () => controller.abort();
    }
  }, [isFolder, isExpanded, item.id, getItemsByParentId, itemsCache]);

  const children = itemsCache[item.id] || [];
  const isLoaded = item.id in (itemsCache || {});
  const hasMore = children.length < (itemsTotals?.[item.id] || 0);

  const handleLoadMore = async (e) => {
    e.stopPropagation();
    setLoadingMore(true);
    const controller = new AbortController();
    const nextPage = Math.floor(children.length / TREE_PER_PAGE) + 1;
    await getItemsByParentId(item.id, controller, nextPage, TREE_PER_PAGE);
    setLoadingMore(false);
  };

  const toggleExpand = () => {
    if (isFolder) {
      setIsExpanded((prev) => !prev);
    }
  };

  const icon = isFolder ? faFolderBlank : faFile;

  return (
    <li
      ref={isFolder ? drop : null}
      className={`folder-item ${isActive ? 'folder-item--active' : ''}`}
    >
      <div
        className={`folder-item__row ${isOver ? 'folder-item__row--hovered' : ''}`}
        style={{ paddingLeft: `${level * 1}rem` }}
      >
        <div
          className="folder-item__header"
          onDoubleClick={() => onNavigate(item)}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openContextMenu(e, item);
          }}
        >
          <FontAwesomeIcon
            icon={icon}
            className={`folder-item__icon ${isFolder ? 'folder-icon' : 'report-icon'}`}
            size="lg"
          />
          <span className="folder-item__name">{item.name}</span>
        </div>
        {isFolder && (
          <div
            className="folder-item__toggle-container"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}
          >
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              className="folder-item__toggle"
            />
          </div>
        )}
      </div>
      {isFolder && isExpanded && (
        <ul className="folder-item__children">
          {isLoaded && children.length === 0 && (
            <li className="folder-item__empty border text-sm rounded ml-4 text-center py-1">
              {t("no-items")}
            </li>
          )}
          {children.map((childItem) => (
            <FolderTreeItem
              key={childItem.id}
              item={childItem}
              getItemsByParentId={getItemsByParentId}
              itemsCache={itemsCache}
              itemsTotals={itemsTotals}
              onNavigate={onNavigate}
              onDrop={onDrop}
              openContextMenu={openContextMenu}
              level={level + 1}
              activeFolderId={activeFolderId}
              activeFolderIds={activeFolderIds}
            />
          ))}
          {hasMore && (
            <li className="folder-item__show-more" style={{ paddingLeft: `${(level + 1) * 1}rem` }}>
              <button
                className="folder-item__show-more-btn"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? '...' : t('show-more')}
              </button>
            </li>
          )}
        </ul>
      )}
    </li>
  );
});

export default FolderTreeItem;
